import styled, { css, keyframes } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import media from '../../../theme/media';
import promoMedia from '../../../theme/promoMedia';

interface PopupOuterProps {
    fullwidthPopup?: boolean;
}

interface PopupBoxProps {
    animated?: boolean;
    fullwidthPopup?: boolean;
    bottom?: string;
}

interface PopupBoxInnerProps {
    fullwidthPopup?: boolean;
    isShowSpinner?: boolean;
}

interface PopupButtonProps {
    disabled?: boolean;
    isAlertButton?: boolean;
    isCentered?: boolean;
}
type PopupButtonThemeProps = PopupButtonProps & { theme: DefaultTheme };
type PopupTheme = DefaultTheme & {
    white: string;
    black: string;
    pink_8_tr: string;
    purple_8_tr: string;
    purple_100: string;
    grey_28_tr: string;
};

const getTheme = (props: { theme: DefaultTheme }): PopupTheme => props.theme as PopupTheme;

interface PopupHeaderProps {
    textAlign?: string;
}

interface PopupButtonsProps {
    withoutBorder?: boolean;
}

interface PopupBodyProps {
    hasPseudoTable?: boolean;
    hasBottomPanel?: boolean;
    textCenter?: boolean;
}

const shouldForwardPopupOuterProp = (prop: string) => !['fullwidthPopup'].includes(prop);
const shouldForwardPopupBoxProp = (prop: string) => !['animated', 'fullwidthPopup', 'bottom'].includes(prop);
const shouldForwardPopupBoxInnerProp = (prop: string) => !['fullwidthPopup', 'isShowSpinner'].includes(prop);
const shouldForwardPopupHeaderProp = (prop: string) => !['textAlign'].includes(prop);
const shouldForwardPopupButtonsProp = (prop: string) => !['withoutBorder'].includes(prop);
const shouldForwardPopupBodyProp = (prop: string) => !['hasPseudoTable', 'hasBottomPanel', 'textCenter'].includes(prop);
const shouldForwardPopupButtonProp = (prop: string) => !['disabled', 'isAlertButton', 'isCentered'].includes(prop);
const shouldForwardPopupSubmitButtonProp = (prop: string) => !['isAlertButton'].includes(prop);

export const getoutAnimation = keyframes`
    0% {
        transform: translateY(0);
    }
    100% {
        transform: translateY(90vh);
    }
`;

export const Overlay = styled.div`
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    bottom: 0;
    display: block;
    justify-content: center;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 10000;
`;

export const PopupOuter = styled.div.withConfig({ shouldForwardProp: shouldForwardPopupOuterProp })<PopupOuterProps>`
    align-items: flex-end;
    display: flex;
    justify-content: center;
    height: 100%;
    padding: ${(props) => (props.fullwidthPopup ? '0' : '0 8px')};

    ${media.tablet720} {
        align-items: center;
        padding: ${(props) => (props.fullwidthPopup ? '0 24px' : '0 8px')};
    }
`;

export const PopupBox = styled.div.withConfig({ shouldForwardProp: shouldForwardPopupBoxProp })<PopupBoxProps>`
    animation-name: ${(props) => (props.animated ? getoutAnimation : '')};
    animation-duration: 0.6s;
    animation-delay: 0.1s;
    animation-timing-function: cubic-bezier(0.68, -0.55, 0.265, 1.55);
    animation-iteration-count: 1;
    background-color: ${(props) => props.theme.white};
    border-radius: ${(props) => (props.fullwidthPopup ? '0' : '12px')};
    overflow: hidden;
    bottom: ${(props) => (props.bottom ? props.bottom : props.fullwidthPopup ? '0' : '24px')};
    max-width: ${(props) => (props.fullwidthPopup ? '888px' : '568px')};
    position: relative;
    width: 100%;

    ${media.tablet720} {
        bottom: auto;
        border-radius: 16px;
    }

    ${media.tablet1080} {
        border-radius: ${(props) => (props.fullwidthPopup ? '12px' : null)};
    }

    ${promoMedia.desktop1440} {
        border-radius: 12px;
    }
`;

export const PopupBoxInner = styled.div.withConfig({ shouldForwardProp: shouldForwardPopupBoxInnerProp })<PopupBoxInnerProps>`
    max-height: ${(props) => (props.fullwidthPopup ? '100vh' : 'calc(100vh - 150px)')};
    overflow-y: ${(props) => (props.isShowSpinner ? 'hidden' : 'auto')};
    position: relative;

    ${media.tablet720} {
        max-height: calc(100vh - 64px);
    }
`;

export const PopupBoxClose = styled.div`
    position: absolute;
    top: 24px;
    right: 24px;
`;

/* Popup Button */
const getHoveredBackground = (props: PopupButtonThemeProps) => {
    const theme = getTheme(props);
    if (props.disabled) {
        return 'none';
    } else {
        return props.isAlertButton ? theme.pink_8_tr : theme.purple_8_tr;
    }
};

const ButtonCommonCss = css<PopupButtonProps>`
    padding: 20px 24px;
    align-items: center;
    display: flex;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    width: 100%;
    font-family: inherit;
    border: none;
    box-sizing: border-box;
    outline: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: top;
    pointer-events: auto;
    font-size: 17px;
    line-height: 24px;
    background: none;
    justify-content: ${(props) => (props.isCentered ? 'center' : null)};

    &:active,
    &:hover:not(:focus) {
        background: ${(props) => getHoveredBackground(props as PopupButtonThemeProps)};
    }

    ${media.tablet720} {
        padding: 28px 32px;
        font-size: 20px;
        line-height: 32px;
    }
`;

export const CommonButton = styled.a.withConfig({ shouldForwardProp: shouldForwardPopupButtonProp })<PopupButtonProps>`
    ${ButtonCommonCss}
`;

export const SubmitButton = styled.button.withConfig({ shouldForwardProp: shouldForwardPopupSubmitButtonProp })<PopupButtonProps>`
    ${ButtonCommonCss}
`;

export const IconWrap = styled.span`
    margin-right: 24px;
    height: 24px;
`;

export const DiskBadgeWrap = styled.span`
    margin-right: 16px;
`;

/* Simple Popup */
export const PopupContent = styled.div`
    font-size: 17px;
    line-height: 24px;
    padding: 72px 0 24px 0;

    ${media.tablet720} {
        font-size: 20px;
        line-height: 32px;
        padding: 88px 0 44px 0;
    }
`;

export const PopupHeader = styled.div.withConfig({ shouldForwardProp: shouldForwardPopupHeaderProp })<PopupHeaderProps>`
    padding: 0 32px 48px 32px;
    text-align: ${(props) => (props.textAlign ? props.textAlign : 'center')};

    /* в простых попапах может быть несколько строк текста, используем тег p */

    p {
        margin-top: 0;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .violetLink {
        color: ${(props) => getTheme(props).purple_100};
    }
`;

/* Попапы с кнопками и текстом имеют разделитель, попапы без текста - нет (withoutBorder) */
export const PopupButtons = styled.div.withConfig({ shouldForwardProp: shouldForwardPopupButtonsProp })<PopupButtonsProps>`
    padding-top: ${(props) => (props.withoutBorder ? '0' : '24px')};
    border-top: ${(props) => (props.withoutBorder ? 'none' : `1px solid ${getTheme(props).grey_28_tr}`)};

    ${media.tablet720} {
        padding-top: ${(props) => (props.withoutBorder ? '0' : '32px')};
    }
`;

/* Complex Popup */
export const PopupBody = styled.div.withConfig({ shouldForwardProp: shouldForwardPopupBodyProp })<PopupBodyProps>`
    padding: ${(props) => (props.hasPseudoTable ? '32px 24px 96px 24px' : props.hasBottomPanel ? '72px 24px 96px 24px' : '72px 24px 24px 24px')};
    font-size: 17px;
    line-height: 24px;
    color: ${(props) => getTheme(props).black};
    text-align: ${(props) => (props.textCenter ? 'center' : '')};

    ${media.tablet720} {
        padding-top: ${(props) => (props.hasPseudoTable ? '48px' : '88px')};
        padding-left: ${(props) => (props.hasPseudoTable ? '48px' : '32px')};
        padding-right: ${(props) => (props.hasPseudoTable ? '48px' : '32px')};
        padding-bottom: ${(props) => (props.hasBottomPanel ? '96px' : '48px')};
        font-size: 20px;
        line-height: 32px;
    }
`;

export const ComplexButtonPanel = styled.div`
    display: flex;
    justify-content: center;
    position: -webkit-sticky;
    position: sticky;
    bottom: 24px;
    margin-top: 32px;
    z-index: 1;

    ${media.tablet720} {
        bottom: 48px;
        margin-top: 48px;
    }
`;

export const ComplexPopupRow = styled.div`
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }

    ${media.tablet720} {
        margin-bottom: 48px;
    }
`;

export const SecondaryButton = styled.div`
    margin-right: 24px;
`;
